import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SxProps, Theme } from '@mui/material';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
} from '@gapartners/ui';
import { useAppDispatch } from '@/store/hooks';
import { addChatMessageNote, addTranscriptNote } from '@/store/api/call';
import { addSegmentNote } from '@/store/slices/callSlice';
import { formatMessageTimestamp } from '@/utils/date';
import type {
  CallMessage,
  TranscriptSegmentDisplay,
  TranscriptSegmentNote,
} from '@/types/call';

export interface TranscriptContentProps {
  previousMessages?: CallMessage[];
  segments: TranscriptSegmentDisplay[];
  contactId: string;
  onNoteAdded?: () => void | Promise<void>;
  showLiveTranscriptLabel?: boolean;
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
  /** Merged over default scroll area styles (height, flex, etc.). */
  scrollBoxSx?: SxProps<Theme>;
  size?: 'default' | 'compact';
  onChatMessageIdsRefresh?: () => void | Promise<void>;
  enableNotes?: boolean;
}

export const TranscriptContent = memo(function TranscriptContent({
  previousMessages = [],
  segments,
  contactId,
  onNoteAdded,
  showLiveTranscriptLabel = false,
  scrollContainerRef,
  scrollBoxSx,
  size = 'default',
  onChatMessageIdsRefresh,
  enableNotes = true,
}: TranscriptContentProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [editingSegmentId, setEditingSegmentId] = useState<string | null>(null);
  const [noteInput, setNoteInput] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);
  const [chatNote404, setChatNote404] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const bodyVariant = size === 'compact' ? 'body2' : 'body1';
  const captionVariant = size === 'compact' ? 'caption' : 'body2';

  const pushLocalNote = (sentenceId: string, text: string) => {
    dispatch(
      addSegmentNote({
        contactId,
        sentenceId,
        note: {
          note: text,
          createdAt: new Date().toISOString(),
        } as TranscriptSegmentNote,
      })
    );
  };

  const handleSaveNote = async (segment: TranscriptSegmentDisplay) => {
    if (!contactId || !noteInput.trim() || !segment.notePayload) return;
    const trimmed = noteInput.trim();
    setNoteError(null);
    setChatNote404(false);
    setSubmitting(true);
    if (segment.notePayload.type === 'local') {
      pushLocalNote(segment.notePayload.localId, trimmed);
      setSubmitting(false);
      setEditingSegmentId(null);
      setNoteInput('');
      return;
    }
    if (segment.notePayload.type === 'chat') {
      const messageId = segment.notePayload.messageId;
      const result = await addChatMessageNote({
        contactId,
        messageId,
        note: trimmed,
      });
      setSubmitting(false);
      if (result.ok) {
        pushLocalNote(`chat-msg:${messageId}`, trimmed);
        setEditingSegmentId(null);
        setNoteInput('');
      } else {
        if (result.status === 404) setChatNote404(true);
        setNoteError(
          result.status === 404
            ? t('components.amazonConnect.chatMessageNoteNotFound')
            : (result.errorMessage ?? '')
        );
      }
      return;
    }
    const payload = segment.notePayload;
    const body =
      payload.type === 'live'
        ? {
            contactId,
            note: noteInput.trim(),
            sentenceId: payload.sentenceId,
          }
        : {
            contactId,
            note: noteInput.trim(),
            transcriptId: payload.transcriptId,
            segmentId: payload.segmentId,
          };
    const result = await addTranscriptNote(body);
    setSubmitting(false);
    if (result.ok) {
      if (payload.type === 'live') {
        pushLocalNote(payload.sentenceId, trimmed);
      } else {
        await onNoteAdded?.();
      }
      setEditingSegmentId(null);
      setNoteInput('');
    } else {
      setNoteError(
        result.status === 404
          ? t('components.amazonConnect.transcriptOrSegmentNotFound')
          : (result.errorMessage ?? '')
      );
    }
  };

  return (
    <Box
      ref={scrollContainerRef}
      sx={{
        overflowY: 'auto',
        pr: 0.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        ...(size === 'compact'
          ? { minHeight: 160, maxHeight: 'min(52vh, 38rem)' }
          : { minHeight: 200, maxHeight: '28rem' }),
        ...scrollBoxSx,
      }}
    >
      {previousMessages.length > 0 && (
        <Box sx={{ pb: 2, borderBottom: 1, borderColor: 'divider' }}>
          {previousMessages.map((msg, i) => (
            <Typography
              key={`prev-${i}`}
              variant={bodyVariant}
              sx={{
                mb: 1,
                color:
                  msg.talker === 'client' ? 'success.main' : 'primary.main',
                fontWeight: msg.talker === 'client' ? 400 : 600,
                wordBreak: 'break-word',
              }}
            >
              {msg.message ?? ''}
            </Typography>
          ))}
        </Box>
      )}
      {segments.length > 0 && (
        <>
          {showLiveTranscriptLabel && previousMessages.length > 0 && (
            <Typography
              variant={captionVariant}
              fontWeight={600}
              color="text.secondary"
              sx={{ pt: 0.5 }}
            >
              {t('components.amazonConnect.liveTranscript')}
            </Typography>
          )}
          {noteError && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 0.5,
                mb: 1,
              }}
            >
              <Typography variant={captionVariant} color="error">
                {noteError}
              </Typography>
              {chatNote404 && onChatMessageIdsRefresh && (
                <Button
                  variant="text"
                  size="small"
                  color="error"
                  sx={{ textTransform: 'none', minWidth: 'auto', p: 0.5 }}
                  onClick={async () => {
                    setNoteError(null);
                    setChatNote404(false);
                    await onChatMessageIdsRefresh();
                  }}
                >
                  {t('components.amazonConnect.refreshChatMessageIds')}
                </Button>
              )}
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {segments.map(segment => {
              const resolveSpeakerType = (): typeof segment.speakerType => {
                if (segment.speakerType) return segment.speakerType;
                if (segment.speaker === 'agent_2') return 'agent_2';
                if (segment.speaker === 'agent') return 'agent';
                if (segment.speaker === 'customer') return 'customer';
                if (segment.speaker === 'bot') return 'bot';
                if (segment.speaker === 'summary') return 'summary';
                return undefined;
              };

              const speakerType = resolveSpeakerType();

              const getSpeakerTag = (): string => {
                if (speakerType === 'agent') {
                  return t('components.amazonConnect.agentLabel');
                }
                if (speakerType === 'agent_2') {
                  return t('components.amazonConnect.consultAgentLabel');
                }
                if (speakerType === 'customer') {
                  return t('components.amazonConnect.clientLabel');
                }
                if (speakerType === 'bot') {
                  return t('components.amazonConnect.bot');
                }
                if (speakerType === 'summary') {
                  return t('components.amazonConnect.summaryLabel');
                }
                return t('components.amazonConnect.clientLabel');
              };
              const getSpeakerTaggedName = (tag: string): string => {
                if (speakerType === 'bot' || speakerType === 'summary') {
                  return tag;
                }
                return `${tag} ${speakerName ? `(${speakerName})` : ''}`;
              };

              const tag = getSpeakerTag();
              const speakerName = segment.speakerName;
              const speakerTaggedName = getSpeakerTaggedName(tag);
              const canAddNote =
                enableNotes &&
                !!segment.notePayload &&
                !!contactId &&
                !segment.isPartial;
              const isEditing = editingSegmentId === segment.id;
              const isAgentLike =
                speakerType === 'agent' ||
                speakerType === 'agent_2' ||
                speakerType === 'bot';
              const lineColor = (() => {
                if (segment.isPartial) return 'text.secondary';
                if (speakerType === 'summary') return 'warning.main';
                if (speakerType === 'agent_2') return 'info.main';
                if (isAgentLike) return 'primary.main';
                return 'success.main';
              })();
              const lineWeight = (() => {
                if (segment.isPartial) return 400;
                if (speakerType === 'summary') return 600;
                if (isAgentLike) return 600;
                return 500;
              })();
              return (
                <Box key={segment.id}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'baseline',
                      gap: 1,
                    }}
                  >
                    <Typography
                      component="span"
                      variant={bodyVariant}
                      sx={{
                        color: lineColor,
                        fontWeight: lineWeight,
                        fontStyle: segment.isPartial ? 'italic' : undefined,
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      <Typography
                        component="span"
                        variant={bodyVariant}
                        fontWeight={700}
                        color="text.primary"
                        sx={{ mr: 0.5 }}
                      >
                        {speakerTaggedName}:
                      </Typography>
                      {!segment.isPartial && segment.timestamp && (
                        <Typography
                          component="span"
                          variant={captionVariant}
                          color="text.secondary"
                          sx={{ mr: 0.5, fontStyle: 'normal' }}
                        >
                          {formatMessageTimestamp(segment.timestamp)}
                        </Typography>
                      )}
                      {segment.text || '\u00a0'}
                    </Typography>
                    {canAddNote && !segment.isPartial && (
                      <Button
                        variant="text"
                        size="small"
                        color="inherit"
                        sx={{
                          textTransform: 'none',
                          minWidth: 'auto',
                          color: 'text.secondary',
                          textDecoration: 'underline',
                          p: 0,
                          fontSize:
                            captionVariant === 'caption'
                              ? '0.75rem'
                              : '0.875rem',
                        }}
                        onClick={() => {
                          setEditingSegmentId(segment.id);
                          setNoteInput('');
                          setNoteError(null);
                        }}
                      >
                        {t('components.amazonConnect.addNote')}
                      </Button>
                    )}
                  </Box>
                  {isEditing && (
                    <Box
                      sx={{
                        mt: 1.5,
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <TextField
                        size="small"
                        fullWidth
                        sx={{ flex: '1 1 200px', minWidth: 160 }}
                        value={noteInput}
                        onChange={e => setNoteInput(e.target.value)}
                        placeholder={t(
                          'components.amazonConnect.notePlaceholder'
                        )}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleSaveNote(segment);
                          if (e.key === 'Escape') {
                            setEditingSegmentId(null);
                            setNoteInput('');
                            setNoteError(null);
                          }
                        }}
                      />
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          disabled={!noteInput.trim() || submitting}
                          onClick={() => handleSaveNote(segment)}
                        >
                          {t('components.amazonConnect.saveNote')}
                        </Button>
                        {submitting && <CircularProgress size={20} />}
                      </Box>
                      <Button
                        variant="text"
                        size="small"
                        color="inherit"
                        sx={{
                          textTransform: 'none',
                          color: 'text.secondary',
                        }}
                        onClick={() => {
                          setEditingSegmentId(null);
                          setNoteInput('');
                          setNoteError(null);
                        }}
                      >
                        {t('components.amazonConnect.cancelNote')}
                      </Button>
                    </Box>
                  )}
                  {segment.notes.length > 0 && (
                    <Box
                      component="ul"
                      sx={{
                        mt: 0.5,
                        ml: 2,
                        pl: 0,
                        listStyle: 'disc',
                        color: 'text.secondary',
                      }}
                    >
                      {segment.notes.map((n, j) => (
                        <Typography
                          key={j}
                          component="li"
                          variant={captionVariant}
                          sx={{ mb: 0.5 }}
                        >
                          {(() => {
                            const creatorName =
                              n.userObject?.userName ??
                              t('components.amazonConnect.noteCreatedByYou');
                            return (
                              <>
                                {t('components.amazonConnect.notePrefix')}
                                <Typography
                                  component="span"
                                  variant={captionVariant}
                                  sx={{ fontStyle: 'italic' }}
                                >
                                  {creatorName}
                                </Typography>
                                {t('components.amazonConnect.noteSuffix')}
                              </>
                            );
                          })()}{' '}
                          {n.note}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
});
